import { CopyOutlined, DeleteOutlined, ExportOutlined } from "@ant-design/icons";
import { SettingsCard } from "admin/account/helpers/settings_card";
import { SettingsTitle } from "admin/account/helpers/settings_title";
import { getDatasetNameRules, layerNameRules } from "admin/dataset/dataset_components";
import { useStartAndPollJob } from "admin/job/job_hooks";
import { startFindLargestSegmentIdJob } from "admin/rest_api";
import {
  Button,
  Col,
  Flex,
  Form,
  type FormInstance,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Tooltip,
} from "antd";
import { FormItemWithInfo } from "dashboard/dataset/helper_components";
import { useWkSelector } from "libs/react_hooks";
import Toast from "libs/toast";
import { BoundingBoxInput, Vector3Input } from "libs/vector_input";
import type React from "react";
import { cloneElement, useEffect } from "react";
import { type APIDataLayer, type APIDataset, APIJobCommand } from "types/api_types";
import type { DataLayer, DataLayerWithTransformations } from "types/schemas/datasource.types";
import { syncValidator, validateTransformationsJSON } from "types/validation";
import { AllUnits, LongUnitToShortUnitMap, type Vector3 } from "viewer/constants";
import type { RotationAndMirroringSettings } from "viewer/model/accessors/dataset_layer_transformation_accessor";
import { getSupportedValueRangeForElementClass } from "viewer/model/bucket_data_handling/data_rendering_logic";
import type { BoundingBoxObject } from "viewer/store";
import {
  AxisRotationSettingForDataset,
  getDatasetBoundingBoxFromLayers,
  getRotationalTransformation,
} from "./dataset_rotation_form_item";
import { useDatasetSettingsContext } from "./dataset_settings_context";

export default function DatasetSettingsDataTab() {
  const { dataset, form } = useDatasetSettingsContext();

  return (
    <div>
      <SettingsTitle title="Data Source" description="Configure the data source" />
      <SimpleDatasetForm dataset={dataset} form={form} />
    </div>
  );
}

function copyDatasetID(datasetId: string | null | undefined) {
  if (!datasetId) {
    return;
  }
  navigator.clipboard.writeText(datasetId);
  Toast.success("Dataset ID copied.");
}

const LEFT_COLUMN_ITEMS_WIDTH = 408;
const COPY_ICON_BUTTON_WIDTH = 32;
const MARGIN_BOTTOM: React.CSSProperties = {
  marginBottom: 24,
};

function AdvancedDatasetTransformationsCard() {
  return (
    <SettingsCard
      title="Transformation Configuration"
      content={
        <Form.Item
          name={["coordinateTransformations"]}
          label={
            <Space size="small">
              Coordinate Transformations JSON.
              <a
                href="https://docs.webknossos.org/webknossos/datasets/settings.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                Read more about the format
              </a>
            </Space>
          }
          rules={[
            {
              validator: (rule, value) =>
                value !== "" ? validateTransformationsJSON(rule, value) : Promise.resolve(),
            },
          ]}
        >
          <Input.TextArea autoSize={{ minRows: 10, maxRows: 20 }} />
        </Form.Item>
      }
      style={MARGIN_BOTTOM}
    />
  );
}

function DatasetTransformationSettingsCard({
  transformationsMode,
  form,
}: {
  transformationsMode: TransformationsMode;
  form: FormInstance;
}) {
  switch (transformationsMode) {
    case TransformationsMode.SIMPLE:
      return (
        <SettingsCard
          title="Axis Rotation"
          style={MARGIN_BOTTOM}
          content={
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <AxisRotationSettingForDataset form={form} />
              </Col>
            </Row>
          }
        />
      );
    case TransformationsMode.ADVANCED:
      return <AdvancedDatasetTransformationsCard />;
    case TransformationsMode.NONE:
    default:
      return null;
  }
}

export enum TransformationsMode {
  NONE = "none",
  SIMPLE = "simple",
  ADVANCED = "advanced",
}

function SimpleDatasetForm({
  form,
  dataset,
}: {
  form: FormInstance;
  dataset: APIDataset | null | undefined;
}) {
  const activeUser = useWkSelector((state) => state.activeUser);
  const onRemoveLayer = (layer: DataLayer) => {
    const oldLayers = form.getFieldValue(["dataSource", "dataLayers"]);
    const newLayers = oldLayers.filter(
      (existingLayer: DataLayer) => existingLayer.name !== layer.name,
    );
    form.setFieldsValue({
      dataSource: {
        dataLayers: newLayers,
      },
    });
  };

  const transformationItems = [
    { value: TransformationsMode.NONE, label: "None" },
    { value: TransformationsMode.SIMPLE, label: "Simple" },
    { value: TransformationsMode.ADVANCED, label: "Advanced" },
  ];

  const dataSource: Record<string, any> = Form.useWatch("dataSource", { form, preserve: true });
  const transformationsMode: TransformationsMode = Form.useWatch(["transformationsMode"], form);
  const coordinateTransformationsJSON: string = Form.useWatch(["coordinateTransformations"], form);

  // If the transformation mode changes, the currently visible transformation settings need to be set
  // in the internal datalayer model of the form.
  useEffect(() => {
    // Prevent unnecessary re-renders by not having the dataSource, which
    // might be changed below, as a dependency
    const currentDataSource: Record<string, any> = form.getFieldValue("dataSource");
    if (currentDataSource == null || currentDataSource.dataLayers?.length === 0) return;
    if (transformationsMode === TransformationsMode.NONE) {
      const dataLayersWithUpdatedTransforms = currentDataSource.dataLayers.map(
        (layer: DataLayer) => {
          return {
            ...layer,
            coordinateTransformations: [],
          };
        },
      );
      form.setFieldValue(["dataSource", "dataLayers"], dataLayersWithUpdatedTransforms);
      return;
    }
    if (form.getFieldError(["coordinateTransformations"]).length > 0) {
      return;
    }
    if (transformationsMode === TransformationsMode.SIMPLE) {
      const isRotationOnly = form.getFieldValue(["isRotationOnly"]);
      if (!isRotationOnly) return;
      const rotationValues: {
        x: RotationAndMirroringSettings;
        y: RotationAndMirroringSettings;
        z: RotationAndMirroringSettings;
      } = form.getFieldValue(["datasetRotation"]);
      const datasetBoundingBox = getDatasetBoundingBoxFromLayers(currentDataSource.dataLayers);
      // This won't happen because we check that there are more than 0 layers,
      // so this is just to satisfy the type checker.
      if (datasetBoundingBox == null) {
        throw new Error("Dataset bounding box is undefined. Cannot apply transformations.");
      }
      const transformations = getRotationalTransformation(datasetBoundingBox, rotationValues);
      const dataLayersWithUpdatedTransforms = currentDataSource.dataLayers.map(
        (layer: DataLayer) => {
          return {
            ...layer,
            coordinateTransformations: transformations,
          };
        },
      );
      form.setFieldValue(["dataSource", "dataLayers"], dataLayersWithUpdatedTransforms);
    }
    if (transformationsMode === TransformationsMode.ADVANCED) {
      if (coordinateTransformationsJSON == null) return;
      const layersWithCoordTransformations: DataLayerWithTransformations[] =
        coordinateTransformationsJSON === "" ? [] : JSON.parse(coordinateTransformationsJSON);
      const dataLayersWithUpdatedTransforms = currentDataSource.dataLayers.map(
        (layer: DataLayer) => {
          const coordinateTransformation = layersWithCoordTransformations?.find(
            (ct) => ct.name === layer.name,
          );
          return {
            ...layer,
            coordinateTransformations: coordinateTransformation?.coordinateTransformations || [],
          };
        },
      );
      form.setFieldValue(["dataSource", "dataLayers"], dataLayersWithUpdatedTransforms);
    }
  }, [coordinateTransformationsJSON, form, transformationsMode]);

  return (
    <div>
      <SettingsCard
        title="General Dataset Settings"
        style={MARGIN_BOTTOM}
        content={
          <Row gutter={[24, 24]}>
            <Col span={24} xl={12}>
              <FormItemWithInfo
                // The dataset name is not synced with the datasource.id.name, because datasource.id represents a DataSourceId
                // where datasource.id.name represents the dataset's directoryName and not the dataset's name.
                name={["dataset", "name"]}
                label="Name"
                info="The name of the dataset"
                validateFirst
                rules={getDatasetNameRules(activeUser)}
              >
                <Input
                  style={{
                    width: LEFT_COLUMN_ITEMS_WIDTH,
                  }}
                />
              </FormItemWithInfo>
              <Space size="large" />
              <FormItemWithInfo
                name={["dataset", "id"]}
                label="Dataset ID"
                info="The ID used to identify the dataset. Needed for e.g. Task bulk creation."
              >
                <Space.Compact>
                  <Input
                    value={dataset?.id}
                    style={{
                      width:
                        activeUser?.isSuperUser && dataset
                          ? LEFT_COLUMN_ITEMS_WIDTH - 2 * COPY_ICON_BUTTON_WIDTH
                          : LEFT_COLUMN_ITEMS_WIDTH - COPY_ICON_BUTTON_WIDTH,
                    }}
                    readOnly
                    disabled
                  />
                  {activeUser?.isSuperUser && dataset ? (
                    <Tooltip title="Inspect the full data source JSON response from the server. This is shown to super users only.">
                      <Button
                        href={`/api/datasets/${dataset?.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        icon={<ExportOutlined />}
                      />
                    </Tooltip>
                  ) : null}
                  <Tooltip title="Copy dataset ID">
                    <Button onClick={() => copyDatasetID(dataset?.id)} icon={<CopyOutlined />} />
                  </Tooltip>
                </Space.Compact>
              </FormItemWithInfo>
              <FormItemWithInfo
                name={["transformationsMode"]}
                label="Transformation Mode"
                info={
                  <>
                    You can remove all transformations by selecting "None", add rotational and
                    mirroring transformations in "Simple" mode, or define custom transformations per
                    layer in "Advanced" mode.
                  </>
                }
              >
                <Select options={transformationItems} style={{ width: LEFT_COLUMN_ITEMS_WIDTH }} />
              </FormItemWithInfo>
            </Col>
            <Col span={24} xl={12}>
              <FormItemWithInfo
                name={["dataSource", "scale", "factor"]}
                label="Voxel Size"
                info="The voxel size defines the extent (for x, y, z) of one voxel in the specified unit."
                rules={[
                  {
                    required: true,
                    message: "Please provide a voxel size for the dataset.",
                  },
                  {
                    validator: syncValidator(
                      (value: Vector3) => value?.every((el) => el > 0),
                      "Each component of the voxel size must be greater than 0",
                    ),
                  },
                ]}
              >
                <Vector3Input
                  style={{
                    width: 400,
                  }}
                  allowDecimals
                />
              </FormItemWithInfo>
              <FormItemWithInfo
                name={["dataSource", "scale", "unit"]}
                label="Unit"
                info="The unit in which the voxel size is defined."
                rules={[
                  {
                    required: true,
                    message: "Please provide a unit for the voxel scale of the dataset.",
                  },
                ]}
              >
                <Select
                  style={{ width: 120 }}
                  options={AllUnits.map((unit) => ({
                    value: unit,
                    label: (
                      <span>
                        <Tooltip title={unit}>{LongUnitToShortUnitMap[unit]}</Tooltip>
                      </span>
                    ),
                  }))}
                />
              </FormItemWithInfo>
            </Col>
          </Row>
        }
      />

      <DatasetTransformationSettingsCard transformationsMode={transformationsMode} form={form} />

      {dataSource?.dataLayers?.map((layer: DataLayer, idx: number) => (
        // the layer name may change in this view, the order does not, so idx is the right key choice here
        <Row gutter={[24, 24]} key={`layer-${idx}`}>
          <Col span={24}>
            <SettingsCard
              title={`Layer: ${layer.name}`}
              style={MARGIN_BOTTOM}
              content={
                <SimpleLayerForm
                  dataset={dataset}
                  layer={layer}
                  index={idx}
                  onRemoveLayer={onRemoveLayer}
                  form={form}
                />
              }
            />
          </Col>
        </Row>
      ))}
    </div>
  );
}

function getMags(layer: DataLayer) {
  return layer.mags.map((res) => res.mag);
}

function SimpleLayerForm({
  layer,
  index,
  onRemoveLayer,
  form,
  dataset,
}: {
  layer: DataLayer;
  index: number;
  onRemoveLayer: (layer: DataLayer) => void;
  form: FormInstance;
  dataset: APIDataset | null | undefined;
}) {
  const dataLayers = Form.useWatch(["dataSource", "dataLayers"], form);
  const category = Form.useWatch(["dataSource", "dataLayers", index, "category"], form);

  const layerCategorySavedOnServer = dataset?.dataSource.dataLayers[index]?.category;
  const isStoredAsSegmentationLayer = layerCategorySavedOnServer === "segmentation";
  const isSegmentation = category === "segmentation";
  const valueRange = getSupportedValueRangeForElementClass(layer.elementClass);

  const mayLayerBeRemoved = dataLayers?.length > 1;

  // biome-ignore lint/correctness/useExhaustiveDependencies: Always revalidate in case the user changes the data layers in the form.
  useEffect(() => {
    // Always validate all fields so that in the case of duplicate layer
    // names all relevant fields are properly validated.
    // This is a workaround, since shouldUpdate=true on a
    // FormItemWithInfo doesn't work for some reason.
    form.validateFields();
  }, [dataLayers]);

  const { runningJobs, startJob, mostRecentSuccessfulJob } = useStartAndPollJob({
    onSuccess() {
      Toast.success(
        "The computation of the largest segment id for this dataset has finished. Please reload the page to see it.",
      );
    },
    onFailure() {
      Toast.error(
        "The computation of the largest segment id for this dataset didn't finish properly.",
      );
    },
    initialJobKeyExtractor: (job) =>
      job.command === "find_largest_segment_id" && job.args.datasetName === dataset?.name
        ? (job.args.datasetName ?? "largest_segment_id")
        : null,
  });
  const activeJob = runningJobs[0];

  const startJobFn =
    dataset != null
      ? async () => {
          const job = await startFindLargestSegmentIdJob(dataset.id, layer.name);
          Toast.info(
            "A job was scheduled to compute the largest segment ID. It will be automatically updated for the dataset. You may close this tab now.",
          );
          return [job.args.datasetName ?? "largest_segment_id", job.id] as [string, string];
        }
      : null;

  return (
    <div
      style={{
        width: "100%",
        position: "relative",
      }}
    >
      {mayLayerBeRemoved && (
        <div style={{ position: "absolute", top: 12, right: 0, zIndex: 500 }}>
          <Tooltip title="Remove Layer">
            <Button shape="circle" icon={<DeleteOutlined />} onClick={() => onRemoveLayer(layer)} />
          </Tooltip>
        </div>
      )}
      <Row gutter={48}>
        <Col span={24} xl={12}>
          <FormItemWithInfo
            name={["dataSource", "dataLayers", index, "name"]}
            label="Name"
            style={{
              marginBottom: 24,
            }}
            info="The name of the layer."
            rules={[
              {
                required: true,
                message: "Please provide a valid layer name.",
              },
              ...layerNameRules,
              {
                validator: syncValidator(
                  (value: string) =>
                    form
                      .getFieldValue(["dataSource", "dataLayers"])
                      .filter((someLayer: APIDataLayer) => someLayer.name === value).length <= 1,
                  "Layer names must be unique.",
                ),
              },
            ]}
          >
            <Input
              disabled
              style={{
                width: LEFT_COLUMN_ITEMS_WIDTH,
              }}
            />
          </FormItemWithInfo>

          <Space size="large">
            <FormItemWithInfo
              label="Data Format"
              style={{
                marginBottom: 24,
              }}
              info="The data format of the layer."
            >
              <Select disabled value={layer.dataFormat} style={{ width: 120 }}>
                <Select.Option value={layer.dataFormat}>{layer.dataFormat}</Select.Option>
              </Select>
            </FormItemWithInfo>
            <FormItemWithInfo
              label="Data Type"
              style={{
                marginBottom: 24,
              }}
              info="The data type (sometimes called dtype) of the layer."
            >
              <Select disabled value={layer.elementClass} style={{ width: 120 }}>
                <Select.Option value={layer.elementClass}>{layer.elementClass}</Select.Option>
              </Select>
            </FormItemWithInfo>
            {"numChannels" in layer ? (
              <FormItemWithInfo
                label="Channel Count"
                style={{
                  marginBottom: 24,
                }}
                info="The channel count of the layer."
              >
                <Select disabled value={layer.numChannels} style={{ width: 120 }}>
                  <Select.Option value={layer.numChannels}>{layer.numChannels}</Select.Option>
                </Select>
              </FormItemWithInfo>
            ) : null}
          </Space>

          <FormItemWithInfo
            label="Magnifications"
            style={{
              marginBottom: 24,
            }}
            info="The magnifications of the layer."
          >
            <Select
              mode="multiple"
              disabled
              allowClear
              value={getMags(layer).map((mag) => mag.toString())}
              style={{ width: LEFT_COLUMN_ITEMS_WIDTH }}
            >
              {getMags(layer).map((mag) => (
                <Select.Option key={mag.toString()} value={mag.toString()}>
                  {typeof mag === "number" ? mag : mag.join("-")}
                </Select.Option>
              ))}
            </Select>
          </FormItemWithInfo>
        </Col>
        <Col span={24} xl={12}>
          <FormItemWithInfo
            name={["dataSource", "dataLayers", index, "boundingBox"]}
            label="Bounding box"
            style={{
              marginBottom: 24,
            }}
            info="The bounding box defines the extent of the data in the format x, y, z, width, height, depth (in voxel coordinates)."
            rules={[
              {
                required: true,
                message: "Please define a valid bounding box.",
              },
              {
                validator: syncValidator(
                  (value: BoundingBoxObject) =>
                    value.width !== 0 && value.height !== 0 && value.depth !== 0,
                  "Width, height and depth must not be zero",
                ),
              },
              {
                warningOnly: true,
                validator: (_rule, value) =>
                  value == null || value === ""
                    ? Promise.reject(
                        new Error(
                          "When left empty, annotating this layer later will only be possible with manually chosen segment IDs.",
                        ),
                      )
                    : Promise.resolve(),
              },
            ]}
          >
            <BoundingBoxInput
              style={{
                width: 300,
              }}
            />
          </FormItemWithInfo>

          <Form.Item
            name={["dataSource", "dataLayers", index, "category"]}
            label="Category"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Select the category of the layer"
              style={{
                width: 300,
              }}
            >
              <Select.Option value="color">Color / grayscale</Select.Option>
              <Select.Option value="segmentation">Segmentation</Select.Option>
            </Select>
          </Form.Item>

          {isSegmentation ? (
            <div>
              <Flex align="flex-end">
                <FormItemWithInfo
                  name={["dataSource", "dataLayers", index, "largestSegmentId"]}
                  label="Largest segment ID"
                  info="The largest segment ID specifies the highest id which exists in this segmentation layer. When users extend this segmentation, new IDs will be assigned starting from that value."
                  initialValue={
                    "largestSegmentId" in layer && layer.largestSegmentId != null
                      ? `${layer.largestSegmentId}`
                      : undefined
                  }
                  rules={[
                    {
                      validator: (_rule, value) =>
                        value == null ||
                        value === "" ||
                        (value >= valueRange[0] && value <= valueRange[1] && value !== 0)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                `The largest segmentation ID must be between ${valueRange[0]} and ${valueRange[1]} and not 0. You can also leave this field empty, but annotating this layer later will only be possible with manually chosen segment IDs.`,
                              ),
                            ),
                    },
                    {
                      warningOnly: true,
                      validator: (_rule, value) =>
                        value != null && value === valueRange[1]
                          ? Promise.reject(
                              new Error(
                                `The largest segmentation ID has already reached the maximum possible value of ${valueRange[1]}. Annotations of this dataset cannot create new segments.`,
                              ),
                            )
                          : Promise.resolve(),
                    },
                    {
                      warningOnly: true,
                      validator: (_rule, value) =>
                        value == null || value === ""
                          ? Promise.reject(
                              new Error(
                                "When left empty, annotating this layer later will only be possible with manually chosen segment IDs.",
                              ),
                            )
                          : Promise.resolve(),
                    },
                  ]}
                >
                  <DelegatePropsToFirstChild>
                    <InputNumber
                      // @ts-expect-error returning undefined does work without problems
                      parser={(value: string | undefined) => {
                        if (value == null || value === "") {
                          return undefined;
                        }
                        return Number.parseInt(value, 10);
                      }}
                    />
                    {dataset?.dataStore.jobsSupportedByAvailableWorkers.includes(
                      APIJobCommand.FIND_LARGEST_SEGMENT_ID,
                    ) ? (
                      <Tooltip
                        title={
                          !isStoredAsSegmentationLayer
                            ? "Before being able to detect the largest segment id you must save your changes."
                            : `${
                                activeJob != null ? "Scanning" : "Scan"
                              } the data to derive the value automatically`
                        }
                      >
                        <Button
                          type={mostRecentSuccessfulJob == null ? "primary" : "default"}
                          style={{ marginLeft: 8 }}
                          loading={activeJob != null}
                          disabled={
                            !isStoredAsSegmentationLayer || activeJob != null || startJob == null
                          }
                          onClick={
                            startJob != null && startJobFn != null
                              ? () => startJob(startJobFn)
                              : () => Promise.resolve()
                          }
                        >
                          Detect
                        </Button>
                      </Tooltip>
                    ) : (
                      <span></span>
                    )}
                  </DelegatePropsToFirstChild>
                </FormItemWithInfo>
              </Flex>
              {mostRecentSuccessfulJob && (
                <div style={{ marginTop: -6 }}>
                  Output of most recent job: {mostRecentSuccessfulJob.returnValue}
                </div>
              )}
            </div>
          ) : null}
        </Col>
      </Row>
    </div>
  );
}

function DelegatePropsToFirstChild({ children, ...props }: { children: React.ReactElement[] }) {
  // This is a small helper function which allows us to pass two children two FormItemWithInfo
  // even though antd only demands one. We do this for better layouting.
  return (
    <>
      {cloneElement(children[0], props)}
      {children[1]}
    </>
  );
}
